import Service from './Service';

export class propertySearch {
  searchHost: string;
  pageSize: number;
  apiKey: string;
  indexName: string;
  apiVersion: string;

  constructor(
    searchHost: string,
    apiKey: string,
    apiVersion: string,
    indexName: string,
    pageSize: number = 10
  ) {
    this.searchHost = searchHost;
    this.pageSize = pageSize;
    this.apiKey = apiKey;
    this.apiVersion = apiVersion;
    this.indexName = indexName;
  }

  async search(criteria: SearchCriteria, pageNumber: number = 0): Promise<{}> {
    var service = new Service(this.searchHost, { 'api-key': this.apiKey });

    var body: Body = {
      filter: this.buildFilter(criteria),
      orderby: this.buildOrderBy(criteria),
    };

    if (criteria.suburb) {
      body.search = criteria.suburb;
      body.searchFields = 'address_suburb_or_town';
      body.searchMode = 'all';
    } else if (criteria.postcode) {
      body.search = criteria.postcode;
      body.searchFields = 'address_postcode';
      body.searchMode = 'all';
    } else if (criteria.accountId) {
      body.search = criteria.accountId;
      body.searchFields = 'account_id';
      body.searchMode = 'all';
    } else if (criteria.accountName) {
      body.search = criteria.accountName;
      body.searchFields = 'account_name';
      body.searchMode = 'all';
    }

    if (this.pageSize > 0) {
      body.top = this.pageSize;
      body.skip = pageNumber * this.pageSize;
      body.count = true;
    }

    //console.log("filter: " + body.filter);

    return this.processResult(await service.post<Body, any>(this.url, body));
  }

  async getProperty(id: string): Promise<{}> {
    var service = new Service(this.searchHost, { 'api-key': this.apiKey });

    var body = {
      search: id,
      searchFields: 'default_external_id',
      searchMode: 'all',
    };

    return this.processResult(await service.post(this.url, body));
  }

  private parseCollection(collection) {
    let returnCollection = [];

    if(collection == null) {
      return null;
    }
    
    for (let item of collection) {
      returnCollection.push(JSON.parse(item));
    }
    return returnCollection;
  }

  processResult(res) {
    for (let property of res.value) {

      property.images = this.parseCollection(property.images);
      property.events = this.parseCollection(property.events);
      property.links = this.parseCollection(property.links);
      property.floorplans = this.parseCollection(property.floorplans);

    }
    return res;
  }

  private get url(): string {
    return `/indexes/${this.indexName}/docs/search?api-version=${
      this.apiVersion
      }`;
  }

  private buildFilter(criteria: SearchCriteria): string {
    let filter = '';

    if (criteria.listingType && criteria.listingType === 'Auction') {
      filter = filter.append(`authority_type eq '${criteria.listingType}'`);
    } else if (criteria.listingType) {
      filter = filter.append(
        `listing_sale_or_rental eq '${criteria.listingType}'`
      );
    }

    if (criteria.currentProperties) {
      filter = filter.append("system_listing_state eq 'current'");
    } else {
      filter = filter.append("system_listing_state ne 'current' and system_listing_state ne 'withdrawn' and state_date ne null and state_date ne ''");
    }

    if (criteria.bedrooms) {
      if (criteria.bedrooms.max && criteria.bedrooms.max.toString() !== '') {
        filter = filter.append(
          `attributes_bedrooms le '${criteria.bedrooms.max}'`
        );
      }
      if (criteria.bedrooms.min && criteria.bedrooms.min.toString() !== '') {
        filter = filter.append(
          `attributes_bedrooms ge '${criteria.bedrooms.min}'`
        );
      }
    }
    if (criteria.bathrooms) {
      if (criteria.bathrooms.max && criteria.bathrooms.max.toString() !== '') {
        filter = filter.append(
          `attributes_bathrooms le '${criteria.bathrooms.max}'`
        );
      }
      if (criteria.bathrooms.min && criteria.bathrooms.min.toString() !== '') {
        filter = filter.append(
          `attributes_bathrooms ge '${criteria.bathrooms.min}'`
        );
      }
    }
    if (criteria.garages) {
      if (criteria.garages.max && criteria.garages.max.toString() !== '') {
        filter = filter.append(
          `attributes_garages le '${criteria.garages.max}'`
        );
      }
      if (criteria.garages.min && criteria.garages.min.toString() !== '') {
        filter = filter.append(
          `attributes_garages ge '${criteria.garages.min}'`
        );
      }
    }
    if (criteria.price) {
      if (criteria.price.max && criteria.price.max.toString() !== '') {
        filter = filter.append(`price_match le ${criteria.price.max}`);
      }
      if (criteria.price.min && criteria.price.min.toString() !== '') {
        filter = filter.append(`price_match ge ${criteria.price.min}`);
      }
    }
    if (criteria.street) {
      filter = filter.append(`address_street eq '${criteria.street}'`);
    }
    if (criteria.streetNumber) {
      filter = filter.append(
        `address_street_number eq '${criteria.streetNumber}'`
      );
    }
    if (criteria.unitNumber) {
      filter = filter.append(`address_unit_number eq '${criteria.unitNumber}'`);
    }
    if (criteria.openForInspection) {
      //filter = filter.append(``);
    }
    if (criteria.agentId) {
      filter = filter.append(
        `(listing_agent_1_id eq '${
        criteria.agentId
        }' or listing_agent_2_id eq '${criteria.agentId}')`
      );
    }
    if (criteria.feedStatus) {
      filter = filter.append(
        `feed_status eq '${criteria.feedStatus}'`
      );
    }
    if (criteria.neFeedStatus) {
      filter = filter.append(
        `feed_status ne '${criteria.neFeedStatus}'`
      );
    }

    if (criteria.propertyCategory) {
      filter = filter.append(`property_category eq '${criteria.propertyCategory}'`);
    }

    if (criteria.tag) {
      filter = filter.append(`tags/any(t: t eq '${criteria.tag}')`);
    }

    filter = filter.append(`is_deleted eq false`);

    return filter;
  }

  private buildOrderBy(criteria: SearchCriteria): string {
    if (!criteria.orderBy || criteria.orderBy.length === 0) return '';

    let result = '';

    for (let orderBy of criteria.orderBy) {
      result += `,${orderBy.field} ${orderBy.direction}`;
    }

    return result.substring(1);
  }
}

export interface SearchCriteria {
  listingType: 'Sale' | 'Auction' | 'Rental';
  openForInspection: boolean;
  currentProperties: boolean;
  unitNumber?: string;
  streetNumber?: string;
  street?: string;
  suburb?: string;
  postcode?: string;
  bedrooms?: Range;
  bathrooms?: Range;
  garages?: Range;
  price?: Range;
  agentId?: string;
  orderBy: Array<OrderBy>;
  accountId?: string;
  accountName?: string;
  feedStatus?: string;
  neFeedStatus?: string;
  propertyCategory?: string;
  tag?: string;
}

export interface Range {
  min?: number;
  max?: number;
}

export interface OrderBy {
  field: string;
  direction: 'asc' | 'desc';
}

interface Body {
  filter?: string;
  orderby?: string;
  search?: string;
  searchFields?: string;
  searchMode?: 'any' | 'all';
  top?: number;
  skip?: number;
  count?: boolean;
}

declare global {
  interface String {
    append(string: string): string;
  }
}

String.prototype.append = function (this: string, string: string): string {
  if (this == '') {
    return string;
  }

  return this + ' and ' + string;
};



// WEBPACK FOOTER //
// ./ts/PropertySearch.ts